/**
 * The NEW Yanyu map: three greybox layout options, for the user's choice (review/decide/yanyu2/index.html).
 * Nothing in the game reads this yet. It is the dummy-first plan: districts, terraces, roads and monster areas as
 * plain boxes, grown outward from the demo plaza (layouts/demoPlaza.ts) at the map's centre.
 *
 * Coordinates are metres, x east, y south, like every layout. The core is 600 × 600 m, the plaza fills
 * x 260–340, y 260–340 in all three, so its centre is (300, 300) and its south gate (300, 340).
 * Levels are metres above the valley floor (0). Water's surface is at −0.6.
 *
 * The page's drawings come from tools/yanyu2plan (yanyu2plan.html) and scripts/yanyu2-plan-shots.mjs.
 */

export type Rect = readonly [x0: number, x1: number, y0: number, y1: number];
/** A point; the third value is the level (metres) where a road climbs. */
export type Pt = readonly [x: number, y: number, z?: number];

export type GreyKind =
  | 'sea'
  | 'river'
  | 'lake'
  | 'terrace'
  | 'cliff'
  | 'court'
  | 'market'
  | 'harbour'
  | 'homes'
  | 'shrine'
  | 'hall'
  | 'fields'
  | 'forest'
  | 'rock'
  | 'training'
  | 'wall'
  | 'pier'
  | 'bridge'
  | 'boat'
  | 'falls'
  | 'island';

/** A patch of ground: water, a terrace (from 0 up to `level`), or a district filled with lots or trees. */
export interface GreyArea {
  readonly kind: GreyKind;
  readonly name?: string;
  readonly rect?: Rect;
  readonly poly?: readonly Pt[];
  /** The top of the patch (metres). Water ignores it. */
  readonly level?: number;
  /** Fill with buildings: lot width, depth, gap and wall height (metres). */
  readonly lots?: readonly [w: number, d: number, gap: number, h: number];
  /** Fill with trees, one about every `trees` metres. */
  readonly trees?: number;
}

/** One solid box: a hall, a pier, a wall, a boat. */
export interface GreyBox {
  readonly kind: GreyKind;
  readonly name?: string;
  readonly rect: Rect;
  readonly base?: number;
  readonly h: number;
}

/** A school's compound (wall ring, main hall, side halls, a yard), on its ground at `base`. */
export interface GreyCompound {
  readonly school: 'mist-peak' | 'bell-stone' | 'lantern-road' | 'dim-moon' | 'pine-ink' | 'cinnabar';
  readonly name: string;
  readonly rect: Rect;
  readonly base: number;
}

export interface GreyRoad {
  readonly w: number;
  readonly pts: readonly Pt[];
}

export interface GreyMonster {
  readonly name: string;
  readonly rect: Rect;
  /** Monster levels (ระดับ). */
  readonly levels: string;
}

/** A place the page times the walk to, along `route` from the plaza's centre. */
export interface GreySpot {
  readonly name: string;
  readonly route: readonly Pt[];
}

/** A camera for a ko-view frame: where it looks, how many screen pixels a metre (32 = zoom 1), its turn. */
export interface GreyShot {
  readonly id: string;
  readonly name: string;
  readonly at: Pt;
  readonly ppm: number;
  readonly yaw?: number;
}

/** A region of the later, kilometre-scale map around the core (metres, may lie outside 0–600). */
export interface GreyFuture {
  readonly name: string;
  readonly rect: Rect;
  readonly kind: GreyKind;
}

/**
 * A flight of stairs or a ramp: a rect whose level runs straight from `bottom` at its `down` side to `top` at the
 * opposite side (as the layout builder's flights, layout.ts `flightLevel`).
 */
export interface GreyFlight {
  readonly kind: 'stairs' | 'ramp';
  readonly rect: Rect;
  readonly down: 'n' | 'e' | 's' | 'w';
  readonly bottom: number;
  readonly top: number;
}

/** A flat pad at a level (a stair's landing). */
export interface GreyPad {
  readonly rect: Rect;
  readonly level: number;
}

/**
 * A school's crag: a sheer block from the ground under it to `level`, climbed by stairs along two of its faces
 * (C's way): up the south face from `base` (the ground at its foot), a landing at the `corner`, up the west (`sw`)
 * or east (`se`) face to a landing beside the top's north end. Or leapt with qinggong.
 */
export interface GreyCrag {
  readonly name?: string;
  readonly rect: Rect;
  readonly level: number;
  readonly base: number;
  readonly corner: 'sw' | 'se';
}

/** A stair's width along a crag's face (metres). */
export const CRAG_STAIR = 4;

/** A crag's stairs and landings (see `GreyCrag`). */
export function cragStairs(c: GreyCrag): { flights: GreyFlight[]; pads: GreyPad[] } {
  const [x0, x1, y0, y1] = c.rect;
  const W = CRAG_STAIR;
  const lenA = x1 - x0;
  const lenB = y1 - (y0 + W);
  const mid = Math.round((c.base + ((c.level - c.base) * lenA) / (lenA + lenB)) * 10) / 10;
  const sw = c.corner === 'sw';
  const side: Rect = sw ? [x0 - W, x0, 0, 0] : [x1, x1 + W, 0, 0];
  return {
    flights: [
      // Along the south face, from its far end (the bottom) to the corner.
      { kind: 'stairs', rect: [x0, x1, y1, y1 + W], down: sw ? 'e' : 'w', bottom: c.base, top: mid },
      // Along the side face, from the corner up to the landing at its north end.
      { kind: 'stairs', rect: [side[0], side[1], y0 + W, y1], down: 's', bottom: mid, top: c.level },
    ],
    pads: [
      { rect: [side[0], side[1], y1, y1 + W], level: mid },
      { rect: [side[0], side[1], y0, y0 + W], level: c.level },
    ],
  };
}

export interface GreyPlan {
  readonly id: 'A' | 'B' | 'C';
  readonly name: string;
  readonly tagline: string;
  readonly w: number;
  readonly h: number;
  /** The demo plaza's north-west corner. */
  readonly plaza: readonly [x: number, y: number];
  /** Gates opened in the plaza's wall beside the south one. */
  readonly gates: readonly ('n' | 'e' | 'w')[];
  readonly areas: readonly GreyArea[];
  readonly boxes: readonly GreyBox[];
  readonly compounds: readonly GreyCompound[];
  readonly roads: readonly GreyRoad[];
  /** Schools' crags (each with its stairs, `cragStairs`). */
  readonly crags?: readonly GreyCrag[];
  /** Ramps and stairs between levels, besides the crags'. */
  readonly flights?: readonly GreyFlight[];
  readonly monsters: readonly GreyMonster[];
  readonly spots: readonly GreySpot[];
  readonly shots: readonly GreyShot[];
  readonly future: readonly GreyFuture[];
  readonly notes: readonly string[];
}

const C: Pt = [300, 300];
const S = 600;

// ── A: the bay and shrine hill (the old Yanyu's canon, grown around the plaza) ─────────────────────────────

const PLAN_A: GreyPlan = {
  id: 'A',
  name: 'อ่าวและเขาศาลเจ้า',
  tagline: 'ทะเลทางเหนือ ท่าเรือหน้าลาน ถนนตลาดลงใต้ เขาศาลเจ้าทางตะวันตก สำนักทั้งหกบนเขาขั้นบันไดทางตะวันออกเฉียงเหนือ',
  w: S,
  h: S,
  plaza: [260, 260],
  gates: ['n', 'e', 'w'],
  areas: [
    {
      kind: 'sea',
      name: 'อ่าวหยานหยู',
      poly: [
        [0, 0],
        [600, 0],
        [600, 85],
        [480, 105],
        [400, 130],
        [330, 140],
        [260, 132],
        [200, 108],
        [150, 118],
        [110, 170],
        [60, 215],
        [0, 235],
      ],
    },
    // The shrine hill, three terraces west of the plaza.
    { kind: 'terrace', name: 'เขาศาลเจ้า', rect: [120, 232, 245, 352], level: 6 },
    { kind: 'terrace', rect: [138, 214, 262, 336], level: 14 },
    { kind: 'terrace', rect: [152, 198, 276, 322], level: 22 },
    // The western cliffs and the training ground under them.
    { kind: 'cliff', name: 'ผาตะวันตก', rect: [0, 62, 240, 430], level: 32 },
    { kind: 'cliff', rect: [0, 30, 270, 400], level: 48 },
    { kind: 'training', name: 'ลานฝึก', rect: [70, 112, 360, 420] },
    // The mountain of the six schools, north-east.
    { kind: 'terrace', name: 'เขาหกสำนัก', rect: [420, 600, 128, 334], level: 12 },
    { kind: 'terrace', rect: [458, 600, 138, 282], level: 24 },
    { kind: 'terrace', rect: [512, 600, 148, 238], level: 40 },
    // The harbour and its warehouses, between the plaza and the bay.
    { kind: 'harbour', name: 'ท่าเรือ', rect: [222, 292, 150, 236], lots: [10, 8, 4, 5] },
    { kind: 'harbour', rect: [308, 392, 150, 236], lots: [10, 8, 4, 5] },
    // The market street south of the gate, and its square.
    { kind: 'market', name: 'ถนนตลาด', rect: [268, 295, 350, 470], lots: [8, 10, 2, 5] },
    { kind: 'market', rect: [305, 332, 350, 470], lots: [8, 10, 2, 5] },
    { kind: 'court', name: 'ลานตลาด', rect: [276, 324, 474, 506] },
    // Homes either side of the market, and east of the plaza.
    { kind: 'homes', name: 'ย่านบ้านตะวันตก', rect: [170, 262, 360, 470], lots: [9, 9, 4, 4.5] },
    { kind: 'homes', name: 'ย่านบ้านตะวันออก', rect: [338, 430, 360, 470], lots: [9, 9, 4, 4.5] },
    { kind: 'homes', name: 'บ้านริมเขา', rect: [350, 414, 248, 330], lots: [9, 9, 4, 4.5] },
    // Fields.
    { kind: 'fields', name: 'นาขั้นบันได', rect: [124, 262, 486, 600] },
    { kind: 'fields', name: 'นาตะวันออก', rect: [338, 440, 486, 552] },
    // The wilds.
    { kind: 'forest', name: 'ป่าสนหมาป่า', rect: [446, 600, 344, 520], trees: 9 },
    { kind: 'terrace', name: 'เนินโจร', poly: [[0, 440], [110, 430], [120, 520], [100, 600], [0, 600]], level: 9 },
    { kind: 'forest', rect: [0, 100, 450, 600], trees: 12 },
    { kind: 'rock', name: 'วงผาแดง', rect: [448, 600, 528, 600], trees: 16 },
  ],
  boxes: [
    { kind: 'pier', rect: [246, 252, 104, 152], base: 0.3, h: 0.6 },
    { kind: 'pier', rect: [292, 298, 96, 150], base: 0.3, h: 0.6 },
    { kind: 'pier', rect: [338, 344, 104, 150], base: 0.3, h: 0.6 },
    { kind: 'boat', rect: [256, 262, 112, 128], h: 2 },
    { kind: 'boat', rect: [302, 309, 100, 118], h: 2.5 },
    { kind: 'boat', rect: [348, 354, 118, 132], h: 2 },
    { kind: 'boat', rect: [380, 396, 104, 112], h: 3 },
    { kind: 'shrine', name: 'ศาลเจ้า', rect: [162, 188, 288, 310], base: 22, h: 10 },
    { kind: 'hall', name: 'หอหกสำนัก', rect: [430, 470, 196, 236], base: 12, h: 12 },
    { kind: 'falls', name: 'น้ำตก', rect: [54, 62, 380, 392], base: 0, h: 32 },
    { kind: 'wall', name: 'ประตูเมืองใต้', rect: [180, 296, 556, 560], h: 5 },
    { kind: 'wall', rect: [304, 440, 556, 560], h: 5 },
  ],
  compounds: [
    { school: 'pine-ink', name: 'หอหมึกสน', rect: [426, 470, 262, 302], base: 12 },
    { school: 'lantern-road', name: 'ทางโคมเหล็ก', rect: [478, 522, 290, 330], base: 12 },
    { school: 'cinnabar', name: 'ดาบชาด', rect: [462, 504, 146, 184], base: 24 },
    { school: 'dim-moon', name: 'จันทร์มัว', rect: [466, 506, 240, 278], base: 24 },
    { school: 'mist-peak', name: 'ยอดหมอก', rect: [520, 560, 156, 196], base: 40 },
    { school: 'bell-stone', name: 'หินระฆัง', rect: [560, 598, 196, 234], base: 40 },
  ],
  roads: [
    { w: 8, pts: [[300, 340], [300, 600]] },
    { w: 6, pts: [[300, 260], [300, 140]] },
    { w: 5, pts: [[260, 300], [232, 300, 0], [222, 300, 6], [214, 300, 6], [204, 300, 14], [198, 300, 14], [192, 300, 22]] },
    { w: 6, pts: [[340, 300], [416, 300], [422, 300, 0], [430, 280, 12], [450, 250, 12], [460, 238, 24], [500, 220, 24], [512, 205, 40], [540, 200, 40]] },
    { w: 5, pts: [[300, 410], [170, 410], [112, 400]] },
    { w: 5, pts: [[300, 520], [120, 520], [0, 520, 9]] },
    { w: 5, pts: [[300, 410], [440, 410], [600, 440]] },
    { w: 4, pts: [[420, 300], [440, 340], [520, 528], [540, 600]] },
  ],
  monsters: [
    { name: 'หมาป่า', rect: [480, 580, 370, 470], levels: 'ระดับ 2–4' },
    { name: 'ค่ายโจรภูเขา', rect: [8, 100, 470, 590], levels: 'ระดับ 5–8' },
    { name: 'วงผาแดง', rect: [470, 600, 535, 600], levels: 'ระดับ 8–12' },
  ],
  spots: [
    { name: 'ท่าเรือ', route: [C, [300, 260], [300, 150]] },
    { name: 'ลานตลาด', route: [C, [300, 340], [300, 490]] },
    { name: 'ศาลเจ้า (ยอดเขา 22 ม.)', route: [C, [260, 300], [190, 300]] },
    { name: 'หอหกสำนัก', route: [C, [340, 300], [420, 300], [430, 280], [450, 236]] },
    { name: 'ยอดหมอก (40 ม.)', route: [C, [340, 300], [420, 300], [430, 280], [450, 250], [460, 238], [500, 220], [512, 205], [540, 196]] },
    { name: 'ป่าหมาป่า', route: [C, [340, 300], [420, 300], [440, 340], [500, 420]] },
    { name: 'ค่ายโจร', route: [C, [300, 340], [300, 520], [100, 520], [60, 520]] },
    { name: 'ประตูเมืองใต้', route: [C, [300, 340], [300, 558]] },
  ],
  shots: [
    { id: 'gate', name: 'หน้าประตูใต้ของลาน มองถนนตลาด', at: [300, 360], ppm: 32 },
    { id: 'harbour', name: 'ท่าเรือ', at: [300, 170], ppm: 32 },
    { id: 'mid', name: 'ลานและรอบลาน (ซูมออก 8 พิกเซล/ม.)', at: [320, 300], ppm: 8 },
  ],
  future: [
    { kind: 'sea', name: 'ทะเลและหมู่เกาะ', rect: [-900, 1500, -900, 0] },
    { kind: 'cliff', name: 'เทือกเขาเก้ากระบี่', rect: [600, 1500, -300, 600] },
    { kind: 'fields', name: 'ที่ราบนาและเมืองถัดไป', rect: [0, 1500, 600, 1500] },
    { kind: 'forest', name: 'ป่าไผ่ลึก', rect: [-900, 0, 0, 1500] },
  ],
  notes: [
    'ต่อจากแผนหยานหยูเดิม (อ่าว เขาศาลเจ้า ตลาด สำนักทั้งหก ป่าหมาป่าตะวันออก โจรตะวันตกเฉียงใต้ ผาแดงทางใต้)',
    'ลานเปิดประตูเพิ่มสามทาง (เหนือ ตะวันออก ตะวันตก) นอกจากประตูใต้เดิม',
    'ความสูงไว้เล่นวิชาตัวเบา: เขาศาลเจ้า 6/14/22 ม. เขาหกสำนัก 12/24/40 ม. ผาตะวันตก 32/48 ม.',
  ],
};

// ── B: the town on two banks (a river past the plaza to a harbour in the south) ───────────────────────────

const RIVER_W: Pt[] = [
  [366, 0],
  [372, 120],
  [380, 220],
  [372, 300],
  [384, 380],
  [400, 470],
  [380, 540],
  [330, 560],
];
const RIVER_E: Pt[] = [
  [580, 560],
  [470, 540],
  [440, 470],
  [424, 380],
  [412, 300],
  [418, 220],
  [404, 120],
  [396, 0],
];

const PLAN_B: GreyPlan = {
  id: 'B',
  name: 'เมืองสองฝั่งน้ำ',
  tagline: 'แม่น้ำไหลผ่านข้างลานลงทะเลทางใต้ ตลาดสองฝั่ง สะพานสามแห่ง ศาลเจ้าบนเกาะกลางน้ำ สำนักบนเนินสองฝั่งทางเหนือ',
  w: S,
  h: S,
  plaza: [260, 260],
  gates: ['n', 'e', 'w'],
  areas: [
    { kind: 'sea', name: 'ปากอ่าว', poly: [[0, 568], [200, 560], [330, 556], [580, 556], [600, 560], [600, 600], [0, 600]] },
    { kind: 'river', name: 'แม่น้ำหยานหยู', poly: [...RIVER_W, ...RIVER_E] },
    // The island a step above the water (walked onto from its bridge).
    { kind: 'island', name: 'เกาะศาลเจ้า', rect: [390, 414, 352, 400], level: 0.6 },
    // The foothill shelves of the schools, north-west and north-east; the schools' crags rise from them (`crags`).
    { kind: 'terrace', name: 'เนินตะวันตกเฉียงเหนือ', rect: [30, 250, 30, 220], level: 15 },
    { kind: 'terrace', name: 'เนินตะวันออกเฉียงเหนือ', rect: [450, 600, 20, 200], level: 20 },
    { kind: 'forest', name: 'ป่าต้นน้ำ', rect: [252, 362, 0, 110], trees: 10 },
    { kind: 'forest', rect: [420, 450, 0, 120], trees: 10 },
    // Market on both banks.
    { kind: 'market', name: 'ตลาดฝั่งตะวันตก', rect: [340, 360, 180, 420], lots: [8, 10, 2, 5] },
    { kind: 'market', name: 'ตลาดฝั่งตะวันออก', rect: [444, 464, 204, 420], lots: [8, 10, 2, 5] },
    { kind: 'harbour', name: 'ท่าเรือปากแม่น้ำ', rect: [250, 360, 500, 552], lots: [10, 8, 4, 5] },
    { kind: 'harbour', rect: [470, 560, 500, 540], lots: [10, 8, 4, 5] },
    // Homes.
    { kind: 'homes', name: 'ย่านบ้านตะวันตก', rect: [150, 252, 250, 420], lots: [9, 9, 4, 4.5] },
    { kind: 'homes', name: 'ย่านบ้านใต้ลาน', rect: [250, 340, 360, 470], lots: [9, 9, 4, 4.5] },
    { kind: 'homes', name: 'ย่านบ้านฝั่งตะวันออก', rect: [468, 546, 220, 420], lots: [9, 9, 4, 4.5] },
    { kind: 'fields', name: 'ทุ่งนาริมน้ำ', rect: [20, 140, 240, 470] },
    // The wilds.
    { kind: 'terrace', name: 'เนินโจร', rect: [20, 230, 480, 552], level: 7 },
    { kind: 'forest', rect: [20, 230, 480, 552], trees: 12 },
    { kind: 'rock', name: 'ผาแดงตะวันออก', rect: [548, 600, 220, 500], trees: 14 },
  ],
  boxes: [
    // Bridges a step above the banks (walked straight on).
    { kind: 'bridge', name: 'สะพานเหนือ', rect: [362, 412, 146, 154], base: 0, h: 0.3 },
    { kind: 'bridge', name: 'สะพานลาน', rect: [362, 420, 296, 304], base: 0, h: 0.3 },
    { kind: 'bridge', name: 'สะพานใต้', rect: [362, 446, 446, 454], base: 0, h: 0.3 },
    { kind: 'bridge', rect: [365, 392, 374, 378], base: 0, h: 0.3 },
    { kind: 'shrine', name: 'ศาลเจ้ากลางน้ำ', rect: [394, 410, 360, 380], base: 0.6, h: 9 },
    { kind: 'hall', name: 'หอหกสำนัก', rect: [272, 328, 190, 232], h: 12 },
    { kind: 'pier', rect: [280, 286, 552, 590], base: 0, h: 0.3 },
    { kind: 'pier', rect: [326, 332, 552, 594], base: 0, h: 0.3 },
    { kind: 'pier', rect: [500, 506, 540, 590], base: 0, h: 0.3 },
    { kind: 'boat', rect: [290, 296, 562, 580], h: 2.5 },
    { kind: 'boat', rect: [336, 342, 566, 582], h: 2 },
    { kind: 'boat', rect: [388, 394, 316, 332], h: 2 },
    { kind: 'boat', rect: [410, 418, 500, 518], h: 2.5 },
    { kind: 'falls', name: 'น้ำตกต้นน้ำ', rect: [376, 392, 0, 6], h: 24 },
  ],
  compounds: [
    { school: 'mist-peak', name: 'ยอดหมอก', rect: [80, 124, 70, 112], base: 90 },
    { school: 'bell-stone', name: 'หินระฆัง', rect: [146, 196, 74, 120], base: 75 },
    { school: 'pine-ink', name: 'หอหมึกสน', rect: [60, 110, 168, 202], base: 55 },
    { school: 'lantern-road', name: 'ทางโคมเหล็ก', rect: [460, 496, 140, 190], base: 60 },
    { school: 'dim-moon', name: 'จันทร์มัว', rect: [516, 560, 44, 88], base: 100 },
    { school: 'cinnabar', name: 'ดาบชาด', rect: [550, 590, 140, 190], base: 80 },
  ],
  // Each school on its own crag, 55–100 m, with a gap of a few metres between neighbours to leap.
  crags: [
    { name: 'ผายอดหมอก', rect: [70, 134, 60, 122], level: 90, base: 15, corner: 'sw' },
    { name: 'ผาหินระฆัง', rect: [138, 204, 66, 128], level: 75, base: 15, corner: 'se' },
    { name: 'ผาบัณฑิต', rect: [52, 118, 160, 210], level: 55, base: 15, corner: 'se' },
    { name: 'ผาตะเกียงเหล็ก', rect: [452, 504, 132, 196], level: 60, base: 20, corner: 'se' },
    { name: 'ผาจันทร์มัว', rect: [508, 568, 36, 96], level: 100, base: 20, corner: 'sw' },
    { name: 'ผาดาบชาด', rect: [542, 598, 132, 196], level: 80, base: 20, corner: 'sw' },
  ],
  flights: [
    // Up to the north-west shelf (15 m), north from the west road.
    { kind: 'ramp', rect: [132, 138, 220, 250], down: 's', bottom: 0, top: 15 },
    // Up to the north-east shelf (20 m), north along its west foot from the east bank.
    { kind: 'ramp', rect: [444, 450, 160, 200], down: 's', bottom: 0, top: 20 },
    // Up to the bandits' hill (7 m), west from the road south of the plaza.
    { kind: 'ramp', rect: [230, 250, 494, 500], down: 'e', bottom: 0, top: 7 },
  ],
  roads: [
    { w: 6, pts: [[340, 300], [365, 300]] },
    { w: 6, pts: [[420, 300], [440, 300]] },
    { w: 6, pts: [[362, 0], [362, 540]] },
    { w: 6, pts: [[440, 120], [440, 470], [470, 520]] },
    { w: 8, pts: [[300, 340], [300, 360]] },
    { w: 6, pts: [[300, 260], [300, 236]] },
    { w: 5, pts: [[260, 300], [135, 300], [135, 250]] },
    { w: 5, pts: [[135, 220, 15], [135, 217, 15], [48, 217, 15], [48, 212, 15]] },
    { w: 3, pts: [[136, 217, 15], [136, 128, 15]] },
    { w: 5, pts: [[412, 150], [440, 150]] },
    { w: 5, pts: [[440, 200], [447, 200]] },
    { w: 5, pts: [[447, 160, 20], [447, 154, 20], [470, 154, 20], [470, 200, 20]] },
    { w: 5, pts: [[300, 470], [300, 497], [250, 497]] },
    { w: 5, pts: [[230, 497, 7], [60, 497, 7]] },
  ],
  monsters: [
    { name: 'หมาป่า', rect: [256, 360, 10, 104], levels: 'ระดับ 2–4' },
    { name: 'ค่ายโจรเนินใต้', rect: [30, 220, 490, 548], levels: 'ระดับ 5–8' },
    { name: 'ผาแดง', rect: [550, 600, 240, 490], levels: 'ระดับ 8–12' },
  ],
  spots: [
    { name: 'ตลาดฝั่งตะวันออก (ข้ามสะพาน)', route: [C, [340, 300], [440, 300]] },
    { name: 'ท่าเรือปากแม่น้ำ', route: [C, [300, 340], [300, 520]] },
    { name: 'ศาลเจ้ากลางน้ำ', route: [C, [340, 300], [362, 300], [362, 376], [400, 376]] },
    { name: 'หอหกสำนัก', route: [C, [300, 236]] },
    { name: 'ยอดหมอก (บันไดผา 90 ม.)', route: [C, [260, 300], [135, 300], [135, 217], [136, 124], [68, 124], [68, 62], [100, 62]] },
    { name: 'จันทร์มัว (บันไดผา 100 ม.)', route: [C, [340, 300], [362, 300], [362, 200], [447, 200], [447, 156], [570, 156], [570, 98], [506, 98], [506, 38], [538, 38]] },
    { name: 'ป่าหมาป่าต้นน้ำ', route: [C, [340, 300], [362, 300], [362, 60]] },
    { name: 'ค่ายโจร', route: [C, [300, 340], [300, 480], [140, 500]] },
  ],
  shots: [
    { id: 'gate', name: 'สะพานหน้าลาน ข้ามไปตลาดฝั่งตะวันออก', at: [380, 310], ppm: 32 },
    { id: 'harbour', name: 'ท่าเรือปากแม่น้ำ', at: [300, 540], ppm: 32 },
    { id: 'crags', name: 'ผาสำนักฝั่งตะวันออกเฉียงเหนือ บันไดเลียบหน้าผา (ซูมออก 5 พิกเซล/ม.)', at: [525, 118, 50], ppm: 5 },
    { id: 'mid', name: 'ลานและรอบลาน (ซูมออก 8 พิกเซล/ม.)', at: [340, 300], ppm: 8 },
  ],
  future: [
    { kind: 'sea', name: 'ทะเลใต้และเกาะ', rect: [-900, 1500, 600, 1500] },
    { kind: 'cliff', name: 'ต้นน้ำ เทือกเขาเก้ากระบี่', rect: [-300, 900, -900, 0] },
    // The higher ridge for qinggong (the user's call): a long spine of peaks, 200–400 m, along the north-east.
    { kind: 'rock', name: 'สันเขาเหินฟ้า (200–400 ม.)', rect: [900, 1500, -900, -300] },
    { kind: 'fields', name: 'ที่ราบตะวันตก', rect: [-900, 0, -300, 600] },
    { kind: 'forest', name: 'ป่าตะวันออก เมืองถัดไป', rect: [600, 1500, -900, 600] },
  ],
  notes: [
    'แม่น้ำกว้าง 30–60 ม. ให้เล่นวิชาตัวเบาเหยียบน้ำข้ามได้ สะพานสามแห่งสำหรับคนเดิน',
    'ทะเลย้ายไปอยู่ใต้ (ท่าเรือที่ปากแม่น้ำ) ต่างจากหยานหยูเดิมที่ทะเลอยู่เหนือ',
    'สำนักแต่ละแห่งอยู่บนผาของตัวเอง 55–100 ม. (แบบ C) บนเนินฐาน 15 ม. (ตะวันตกเฉียงเหนือ) และ 20 ม. (ตะวันออกเฉียงเหนือ) ขึ้นด้วยบันไดเลียบหน้าผา หรือเหาะข้ามช่องระหว่างผา',
    'ภูมิภาค 2.4 กม. ภายหลัง: เพิ่มสันเขาเหินฟ้าสูง 200–400 ม. ทางตะวันออกเฉียงเหนือ ไว้เล่นวิชาตัวเบา',
  ],
};

// ── C: the terraced valley (the plaza on a valley floor, everything else climbs) ─────────────────────────────

const PLAN_C: GreyPlan = {
  id: 'C',
  name: 'หุบเขาขั้นบันได',
  tagline: 'ลานอยู่ก้นหุบ ภูเขาสามด้านไต่ขึ้นเป็นขั้น สำนักแต่ละแห่งอยู่บนยอดผาของตัวเอง ศาลเจ้าบนยอดสูงสุด ทะเลสาบทางใต้',
  w: S,
  h: S,
  plaza: [260, 260],
  gates: ['n', 'e', 'w'],
  areas: [
    {
      kind: 'lake',
      name: 'ทะเลสาบหยานหยู',
      poly: [[150, 600], [160, 520], [240, 500], [360, 500], [440, 520], [460, 600]],
    },
    // The west range.
    { kind: 'cliff', name: 'เทือกตะวันตก', rect: [0, 160, 0, 600], level: 30 },
    { kind: 'cliff', rect: [0, 112, 0, 600], level: 60 },
    { kind: 'cliff', rect: [0, 62, 140, 360], level: 100 },
    // The north range and the shrine peak.
    { kind: 'cliff', name: 'เทือกเหนือ', rect: [160, 440, 0, 120], level: 40 },
    { kind: 'cliff', rect: [160, 440, 0, 74], level: 80 },
    { kind: 'cliff', name: 'ยอดศาลเจ้า', rect: [270, 330, 0, 48], level: 120 },
    // The east range, and its terraced fields.
    { kind: 'cliff', name: 'เทือกตะวันออก', rect: [440, 600, 0, 440], level: 25 },
    { kind: 'cliff', rect: [496, 600, 0, 440], level: 55 },
    { kind: 'cliff', rect: [552, 600, 0, 260], level: 90 },
    { kind: 'terrace', name: 'นาขั้นบันได', rect: [440, 600, 440, 520], level: 8 },
    { kind: 'terrace', rect: [490, 600, 440, 490], level: 16 },
    { kind: 'fields', rect: [444, 600, 444, 520] },
    // The valley floor's districts.
    { kind: 'homes', name: 'ย่านบ้านตะวันตก', rect: [172, 252, 170, 430], lots: [9, 9, 4, 4.5] },
    { kind: 'homes', name: 'ย่านบ้านตะวันออก', rect: [348, 430, 170, 430], lots: [9, 9, 4, 4.5] },
    { kind: 'market', name: 'ถนนตลาดลงทะเลสาบ', rect: [268, 295, 350, 470], lots: [8, 10, 2, 5] },
    { kind: 'market', rect: [305, 332, 350, 470], lots: [8, 10, 2, 5] },
    { kind: 'harbour', name: 'ท่าเรือทะเลสาบ', rect: [232, 368, 474, 500], lots: [10, 8, 4, 5] },
    // The wilds, behind the ridges.
    { kind: 'forest', name: 'ป่าหลังเขาตะวันตก', rect: [0, 112, 400, 600], trees: 10 },
    { kind: 'forest', name: 'ป่าสนเหนือ', rect: [440, 600, 0, 120], trees: 11 },
    { kind: 'rock', name: 'หุบผาแดง', rect: [500, 600, 280, 420], trees: 15 },
    { kind: 'fields', name: 'นาริมทะเลสาบ', rect: [160, 230, 440, 520] },
  ],
  boxes: [
    { kind: 'shrine', name: 'ศาลเจ้ายอดเขา', rect: [286, 314, 10, 34], base: 120, h: 10 },
    { kind: 'hall', name: 'หอหกสำนัก', rect: [276, 324, 140, 176], h: 12 },
    { kind: 'falls', name: 'น้ำตกตะวันตก', rect: [152, 160, 470, 484], h: 30 },
    { kind: 'falls', name: 'น้ำตกเหนือ', rect: [196, 208, 112, 120], h: 40 },
    { kind: 'pier', rect: [270, 276, 500, 560], base: 0.3, h: 0.6 },
    { kind: 'pier', rect: [324, 330, 500, 556], base: 0.3, h: 0.6 },
    { kind: 'boat', rect: [282, 288, 516, 532], h: 2 },
    { kind: 'boat', rect: [340, 346, 520, 538], h: 2.5 },
    { kind: 'boat', rect: [220, 228, 540, 556], h: 2 },
  ],
  compounds: [
    { school: 'mist-peak', name: 'ยอดหมอก', rect: [176, 226, 14, 62], base: 80 },
    { school: 'cinnabar', name: 'ดาบชาด', rect: [376, 426, 14, 62], base: 80 },
    { school: 'bell-stone', name: 'หินระฆัง', rect: [6, 56, 180, 230], base: 100 },
    { school: 'dim-moon', name: 'จันทร์มัว', rect: [116, 156, 240, 290], base: 30 },
    { school: 'lantern-road', name: 'ทางโคมเหล็ก', rect: [508, 548, 300, 360], base: 55 },
    { school: 'pine-ink', name: 'หอหมึกสน', rect: [448, 488, 200, 250], base: 25 },
  ],
  roads: [
    { w: 8, pts: [[300, 340], [300, 500]] },
    { w: 6, pts: [[300, 260], [300, 176]] },
    // The long stair to the shrine: switchbacks up the north range.
    { w: 4, pts: [[300, 140], [300, 124, 0], [260, 124, 40], [260, 116, 40], [340, 92, 40], [300, 74, 80], [300, 56, 80], [334, 52, 80], [294, 52, 120], [294, 40, 120]] },
    { w: 4, pts: [[260, 300], [172, 300], [164, 270, 30], [156, 265, 30]] },
    { w: 4, pts: [[340, 300], [436, 300], [446, 250, 25], [488, 230, 25], [500, 300, 55], [508, 330, 55]] },
    { w: 4, pts: [[260, 116, 40], [200, 80, 80]] },
    { w: 4, pts: [[340, 92, 40], [400, 74, 80]] },
    { w: 4, pts: [[300, 500], [440, 480, 8]] },
  ],
  monsters: [
    { name: 'หมาป่า', rect: [10, 108, 420, 590], levels: 'ระดับ 2–4' },
    { name: 'โจรภูเขา', rect: [450, 596, 10, 110], levels: 'ระดับ 5–8' },
    { name: 'หุบผาแดง', rect: [504, 596, 290, 416], levels: 'ระดับ 8–12' },
  ],
  spots: [
    { name: 'ท่าเรือทะเลสาบ', route: [C, [300, 340], [300, 490]] },
    { name: 'หอหกสำนัก', route: [C, [300, 176]] },
    { name: 'ศาลเจ้ายอดเขา (120 ม.)', route: [C, [300, 124], [260, 124], [260, 116], [340, 92], [300, 74], [300, 56], [334, 52], [294, 52], [294, 40]] },
    { name: 'ยอดหมอก (80 ม.)', route: [C, [300, 124], [260, 124], [260, 116], [200, 80]] },
    { name: 'ทางโคมเหล็ก (55 ม.)', route: [C, [340, 300], [436, 300], [446, 250], [488, 230], [500, 300], [508, 330]] },
    { name: 'ป่าหมาป่า (หลังเขา)', route: [C, [260, 300], [172, 300], [160, 268], [100, 420]] },
    { name: 'นาขั้นบันได', route: [C, [300, 340], [300, 500], [440, 480]] },
  ],
  shots: [
    { id: 'gate', name: 'หน้าประตูใต้ของลาน มองถนนตลาด', at: [300, 360], ppm: 32 },
    { id: 'harbour', name: 'บันไดขึ้นเทือกเหนือถึงยอดศาลเจ้า (ซูมออก 5 พิกเซล/ม.)', at: [300, 70, 60], ppm: 5 },
    { id: 'mid', name: 'ลานและรอบลาน (ซูมออก 8 พิกเซล/ม.)', at: [300, 260], ppm: 8 },
  ],
  future: [
    { kind: 'lake', name: 'ทะเลสาบใหญ่และแม่น้ำออกทะเล', rect: [-300, 900, 600, 1500] },
    { kind: 'cliff', name: 'เทือกเขาเก้ากระบี่ (หลังเทือกเหนือ)', rect: [-900, 1500, -900, 0] },
    { kind: 'forest', name: 'ป่าหลังเขาตะวันตก', rect: [-900, 0, 0, 1500] },
    { kind: 'fields', name: 'ที่ราบตะวันออก เมืองถัดไป', rect: [600, 1500, 0, 1500] },
  ],
  notes: [
    'แนวตั้งมากที่สุด: ผา 25–120 ม. สำนักแต่ละแห่งอยู่บนผาของตัวเอง ทางขึ้นเป็นบันไดยาว หรือไต่ผาด้วยวิชาตัวเบา',
    'ไม่มีทะเล มีทะเลสาบแทน (ท่าเรือทะเลสาบ) ต่างจากชื่อร้าน "ลมทะเล" ในลาน',
    'พื้นที่ราบใช้ได้จริงน้อยลง (ก้นหุบราว 280 × 380 ม.)',
  ],
};

export const YANYU2_PLANS: readonly GreyPlan[] = [PLAN_A, PLAN_B, PLAN_C];
